import { Button, Cell, DialogOverlay, DialogOverlayActions, DialogOverlayFooter, Grid, TextField } from '@fs/zion-ui'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../hooks/useNotifications'
import { postFactConclusion } from '../../services/personService'
import { DmcDatePicker } from '../common/DmcDatePicker/DmcDatePicker'
import initDateStandards from '../common/DmcDateStandards'
import DmcPlaceStandards, { initPlaceStandards } from '../common/DmcPlaceStandards'
import { PersonContext } from '../person/PersonContext'
import { makeFactConclusion } from '../person/PersonUtils'

export default function MakeDeadPersonDialogOverlay({ ...overlay }) {
  const { isOpen, transitioning } = overlay
  const { t } = useTranslation()
  const { currentPerson, fetchCurrentPerson } = useContext(PersonContext)
  const notify = useNotifications()

  const initialState = {
    reason: '',
    dateStandards: initDateStandards(),
    placeStandards: initPlaceStandards(),
  }
  const focusElementRef = useRef()
  const [state, updateState] = useState(initialState)
  const actions = {
    reset: () => {
      state.dateStandards = ''
      state.placeStandards = ''
      state.reason = null
      updateState(state)
    },
    setReason: (reason) => {
      updateState((prevState) => {
        return { ...prevState, reason }
      })
    },
    setDateStandards: (dateStandards) => {
      updateState((prevState) => {
        return { ...prevState, dateStandards }
      })
    },
    setPlaceStandards: (placeStandards) => {
      updateState((prevState) => {
        return { ...prevState, placeStandards }
      })
    },
    onClickSave: () => {
      overlay.close()
      const factConclusion = makeFactConclusion(
        'Death',
        state.dateStandards?.original,
        state.dateStandards?.primaryText,
        state.placeStandards?.standard?.standardizedText || state.placeStandards?.originalText,
        state.placeStandards?.standard?.id,
        state.reason
      )
      postFactConclusion(currentPerson.id, factConclusion)
        .then(() => {
          fetchCurrentPerson()
        })
        .catch(({ response }) => {
          response.data.notifications.forEach((notification) => {
            notify[notification.level](notification.msg)
          })
        })
    },
    onClickCancel: () => {
      overlay.close()
    },
  }

  useEffect(() => {
    if (!isOpen && transitioning) {
      // The overlay is currently exiting (still transitioning out)
      actions.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, transitioning])

  return (
    <DialogOverlay
      {...overlay}
      focusRef={focusElementRef}
      title={t('person.status.makeDead', 'Make Dead')}
      subtitle={t('action.confirmMakePersonDead', 'Are you sure you want to make this person dead?')}
    >
      <Grid>
        {/* Row */}
        <Cell columns={6}>
          <DmcDatePicker
            disabled={false}
            selectedItem={state.dateStandards}
            setSelectedItem={actions.setDateStandards}
          />
        </Cell>
        <Cell columns={6}>
          <DmcPlaceStandards editable selectedItem={state.placeStandards} setSelectedItem={actions.setPlaceStandards} />
        </Cell>

        {/* Row */}
        <Cell columns={12}>
          <TextField
            ref={focusElementRef}
            clearable
            label={t('reason', 'Reason')}
            value={state.reason}
            onChange={(e) => actions.setReason(e.target.value)}
          />
        </Cell>
      </Grid>
      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button emphasis="high" onClick={actions.onClickSave}>
            {t('action.confirm', 'Confirm')}
          </Button>
          <Button emphasis="medium" onClick={actions.onClickCancel}>
            {t('action.cancel', 'Cancel')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}
